import * as React from "react";
import { Mail, ShieldCheck } from "lucide-react";
import { useForm } from "react-hook-form";

import { useRequestEmailChangeMutation } from "@/api/email-change";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  emailChangeErrorMessage,
  type EmailChangeChannel,
  type EmailChangeFormData,
  type EmailChangeStep,
} from "./types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const defaultValues: EmailChangeFormData = {
  newEmail: "",
  currentPassword: "",
  code: "",
};

const mfaDescription = (channel: EmailChangeChannel): string => {
  if (channel === "sms") return "Enter the six-digit code sent by SMS to continue.";
  return "Enter the six-digit code sent to your email address to continue.";
};

export const EmailChangeDialog = ({ open, onOpenChange }: Props) => {
  const [requestEmailChange, { isLoading }] = useRequestEmailChangeMutation();
  const [step, setStep] = React.useState<EmailChangeStep>("credentials");
  const [channel, setChannel] = React.useState<EmailChangeChannel>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const submissionGenerationRef = React.useRef(0);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EmailChangeFormData>({ defaultValues, shouldUnregister: false });

  React.useEffect(() => {
    if (!open) {
      submissionGenerationRef.current += 1;
      reset(defaultValues);
      setStep("credentials");
      setChannel(null);
      setErrorMessage(null);
    }
  }, [open, reset]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      submissionGenerationRef.current += 1;
      reset(defaultValues);
      setStep("credentials");
      setChannel(null);
      setErrorMessage(null);
    }
    onOpenChange(nextOpen);
  };

  const onSubmit = async (data: EmailChangeFormData) => {
    const submissionGeneration = submissionGenerationRef.current;
    setErrorMessage(null);

    try {
      const result = await requestEmailChange({
        newEmail: data.newEmail,
        currentPassword: data.currentPassword,
        code: step === "mfa" ? data.code : undefined,
      }).unwrap();

      if (submissionGeneration !== submissionGenerationRef.current) return;

      if (result.status === "mfa_required") {
        setChannel(result.channel);
        setStep("mfa");
        return;
      }

      reset(defaultValues);
      setChannel(null);
      setStep("pending");
    } catch (error) {
      if (submissionGeneration !== submissionGenerationRef.current) return;
      setErrorMessage(emailChangeErrorMessage(error));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Change email address</DialogTitle>
          <DialogDescription>
            {step === "credentials"
              ? "Confirm a new sign-in address before your current email changes."
              : step === "mfa"
                ? "Complete your organization’s verification step."
                : "Your current email stays active until confirmation is complete."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          {step === "credentials" ? (
            <div className="space-y-4" aria-labelledby="email-change-credentials-heading">
              <div className="space-y-1">
                <h3 id="email-change-credentials-heading" className="text-sm font-medium">
                  Confirm your credentials
                </h3>
                <p className="text-sm text-muted-foreground">
                  We will send a confirmation link without changing your account immediately.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email-change-new-email">New email address</Label>
                <Input
                  id="email-change-new-email"
                  type="email"
                  autoComplete="email"
                  spellCheck={false}
                  aria-invalid={errors.newEmail ? "true" : "false"}
                  aria-describedby={errors.newEmail ? "email-change-new-email-error" : undefined}
                  {...register("newEmail", {
                    required: "New email address is required.",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Enter a valid email address.",
                    },
                  })}
                />
                {errors.newEmail?.message ? (
                  <p id="email-change-new-email-error" role="alert" className="text-sm text-destructive">
                    {errors.newEmail.message}
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email-change-current-password">Current password</Label>
                <Input
                  id="email-change-current-password"
                  type="password"
                  autoComplete="current-password"
                  aria-invalid={errors.currentPassword ? "true" : "false"}
                  aria-describedby={errors.currentPassword ? "email-change-current-password-error" : undefined}
                  {...register("currentPassword", { required: "Current password is required." })}
                />
                {errors.currentPassword?.message ? (
                  <p id="email-change-current-password-error" role="alert" className="text-sm text-destructive">
                    {errors.currentPassword.message}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {step === "mfa" ? (
            <div className="space-y-4" aria-labelledby="email-change-mfa-heading">
              <div role="status" className="rounded-md border bg-muted/30 p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <div className="space-y-1">
                    <h3 id="email-change-mfa-heading" className="text-sm font-medium">
                      Verification required
                    </h3>
                    <p className="text-sm text-muted-foreground">{mfaDescription(channel)}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email-change-verification-code">Verification code</Label>
                <Input
                  id="email-change-verification-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  spellCheck={false}
                  maxLength={6}
                  aria-invalid={errors.code ? "true" : "false"}
                  aria-describedby={errors.code ? "email-change-verification-code-error" : undefined}
                  {...register("code", {
                    required: "Verification code is required.",
                    pattern: {
                      value: /^\d{6}$/,
                      message: "Enter the six-digit verification code.",
                    },
                  })}
                />
                {errors.code?.message ? (
                  <p id="email-change-verification-code-error" role="alert" className="text-sm text-destructive">
                    {errors.code.message}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {step === "pending" ? (
            <Alert role="status" variant="default">
              <Mail className="h-4 w-4" aria-hidden="true" />
              <h3 className="mb-1 font-medium leading-none tracking-tight">Check your inbox</h3>
              <AlertDescription>
                If the address is eligible, a confirmation link has been sent. Your current email remains active until
                the link is confirmed.
              </AlertDescription>
            </Alert>
          ) : null}

          {errorMessage ? (
            <Alert variant="destructive">
              <h3 className="mb-1 font-medium leading-none tracking-tight">Request not completed</h3>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter className="gap-3 border-t pt-5 sm:space-x-0">
            {step === "pending" ? (
              <Button type="button" onClick={() => handleOpenChange(false)}>
                Done
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading
                    ? step === "mfa"
                      ? "Verifying…"
                      : "Sending…"
                    : step === "mfa"
                      ? "Verify and send link"
                      : "Send confirmation link"}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
